import { Component, OnInit } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';
import { ExistingLinkedFieldSetViewModel } from '../../../../../Models/existingLinkedFieldSetViewModel';
import { TenantService } from '../../../../../Services';
import { LinkedFieldsService } from '../../../../../Services/linkedFields.service';
import { Field, ModuleContent, Step } from '../../Models';
import { FormFlowFieldLinkedFieldMapping } from '../../Models/ModuleDefinitions/FormFlowFieldLinkedFieldMapping';
import { LinkedFieldTreeMapping } from '../../Models/ModuleDefinitions/LinkedFieldTreeMapping';
import { ModuleService } from '../../Services/module.service';
import { MatToolbar } from '@angular/material/toolbar';
import { FormsModule } from '@angular/forms';
import { MatCard, MatCardTitle, MatCardContent, MatCardSubtitle } from '@angular/material/card';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'linked-field-tree-control-configuration-component',
    styleUrls: ['linked-field-tree-control-configuration.component.less'],
    templateUrl: 'linked-field-tree-control-configuration.component.html',
    imports: [MatToolbar, FormsModule, MatCard, MatProgressSpinner, MatCardTitle, MatCardContent, MatCardSubtitle, MatFormField, MatLabel, MatSelect, MatOption, MatButton, MatIconButton, MatIcon]
})
export class LinkedFieldTreeControlConfigurationComponent implements OnInit {
  public allLinkedFields: Field[] = [];
  public selectableLinkedFields: FormFlowFieldLinkedFieldMapping[] = [];
  public selectedLinkedField: FormFlowFieldLinkedFieldMapping;
  public linkedFieldTreeMappings: LinkedFieldTreeMapping[];
  public isLoading: boolean;
  public isSaving: boolean;
  public treeFieldParentPropertyName: string;
  private treeField: Field;
  private linkedFieldSets: ExistingLinkedFieldSetViewModel[];
  private stepId: string;
  private moduleId: string;
  private version: number;
  private module: ModuleContent;
  private step: Step;

  constructor(
    private activatedRoute: ActivatedRoute,
    private tenantService: TenantService,
    private moduleService: ModuleService,
    private linkedFieldService: LinkedFieldsService,
    public snackBar: MatSnackBar,
  ) { }

  public ngOnInit(): void {
    this.isLoading = true;
    this.stepId = this.activatedRoute.snapshot.params.stepId;
    this.moduleId = this.activatedRoute.snapshot.params.moduleId;
    this.version = +this.activatedRoute.snapshot.params.version;
    this.treeFieldParentPropertyName = this.activatedRoute.snapshot.params.fieldId;

    this.moduleService
      .loadModuleContent(
        this.tenantService.selectedTenant,
        this.moduleId,
        this.version,
      )
      .then((module) => {
        this.module = module;
        this.step = this.module.Steps.find((step) => step.StepId === this.stepId);
        this.treeField = this.step.Fields.find((field) => field.FieldId === this.treeFieldParentPropertyName);
        this.linkedFieldTreeMappings = this.treeField.LinkedFieldTreeMappings ? this.treeField.LinkedFieldTreeMappings : [];

        this.linkedFieldService.get(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version)
          .subscribe((existingLinkedFields) => {
            const parentLinkedFields = this.step
              .Fields
              .filter((field) =>
                existingLinkedFields.some((linkedField) =>
                  linkedField.ParentDisplayName === field.FieldName ||
                  linkedField.ChildDisplayName === field.FieldName));

            this.linkedFieldSets = existingLinkedFields;
            this.allLinkedFields = parentLinkedFields;
            this.setSelectableFields();

            this.isLoading = false;
          });
      });
  }

  public getDisplayName(treeField: LinkedFieldTreeMapping): string {
    return this.allLinkedFields.find((field) => field.FieldId === treeField.Name).FieldName;
  }

  public addNewLink(): void {
    this.linkedFieldTreeMappings.push({
      LinkedFieldSetId: this.selectedLinkedField.LinkedFieldSetId,
      Name: this.selectedLinkedField.FieldId,
    } as LinkedFieldTreeMapping);

    this.setSelectableFields();

    this.selectedLinkedField = null;
  }

  public removeFieldFromConfiguration(treeField: LinkedFieldTreeMapping): void {
    const fieldMappingIndex = this.linkedFieldTreeMappings.indexOf(treeField);
    this.linkedFieldTreeMappings.splice(fieldMappingIndex);

    this.setSelectableFields();
  }

  public saveConfiguration(): void {
    this.treeField.LinkedFieldTreeMappings = this.linkedFieldTreeMappings;
    this.isSaving = true;

    const patchField$ = this.moduleService
      .patchField(
        this.tenantService.selectedTenant,
        this.moduleId,
        this.stepId,
        this.treeField).pipe(debounceTime(1000), first())

    const updateField$ = this.moduleService
      .updateField(
        this.treeField,
        this.tenantService.selectedTenant,
        this.moduleId,
        this.version,
        this.stepId).pipe(debounceTime(1000), first())

    const tasks = [patchField$, updateField$];

    forkJoin(tasks).subscribe(
      () => {
        this.showSnackBarMessage('Configuration Saved');
        this.saveComplete()
      },
      (error) => {
        console.error(error);
        this.showSnackBarMessage("Error Saving", true);
        this.saveComplete()
      });
    }

    private saveComplete(): void {
      this.isSaving = false;
    }

  private setSelectableFields(): void {
    if (this.linkedFieldTreeMappings.length > 0) {
      const descendantLinkedFieldSets = this.getDescendantLinkedFieldSets();

      this.selectableLinkedFields = this.allLinkedFields
        .filter((field) =>
          descendantLinkedFieldSets.some((set) => set.ChildDisplayName === field.FieldName))
        .map((linkedField) => {
          return {
            FieldId: linkedField.FieldId,
            FieldName: linkedField.FieldName,
            LinkedFieldSetId: descendantLinkedFieldSets
              .find((set) => set.ChildDisplayName === linkedField.FieldName).LinkedFieldSetId,
          } as FormFlowFieldLinkedFieldMapping;
        });
    } else {
      this.selectableLinkedFields = this.allLinkedFields.map((linkedField) => {
        return {
          FieldId: linkedField.FieldId,
          FieldName: linkedField.FieldName,
          LinkedFieldSetId: null,
        } as FormFlowFieldLinkedFieldMapping;
      });
    }
  }

  private getDescendantLinkedFieldSets(): ExistingLinkedFieldSetViewModel[] {
    const descendantTreeMapping = this.linkedFieldTreeMappings[this.linkedFieldTreeMappings.length - 1];
    const descendantField = this.allLinkedFields.find((field) => field.FieldId === descendantTreeMapping.Name);
    return this.linkedFieldSets.filter((set) => set.ParentDisplayName === descendantField.FieldName);
    }

  private showSnackBarMessage(message: string, error = false) {
    const config = new MatSnackBarConfig();
    config.duration = 10000;
    config.politeness = error ? 'assertive' : 'polite';
    this.snackBar.open(message, 'Close', config);
  }
}
